import { AfterViewInit, Directive, ElementRef, HostListener, Self, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appUppercase]'
})
export class UppercaseDirective implements AfterViewInit {

  private isNativeInput: boolean = false;
  private nestedInput: HTMLInputElement | null = null;
  private isApplying: boolean = false;

  constructor(
    private el: ElementRef,
    @Optional() @Self() private ngControl: NgControl
  ) {}

  ngAfterViewInit(): void {
    const el = this.el.nativeElement as HTMLElement;
    this.isNativeInput = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA';

    if (!this.isNativeInput) {
      // Buscar el input nativo anidado (ej: dentro de ng-autocomplete o componente wrapper)
      // Esperar un tick para que el DOM del componente hijo esté renderizado
      setTimeout(() => {
        this.nestedInput = el.querySelector('input');
        if (this.nestedInput) {
          this.nestedInput.addEventListener('input', () => this.applyUppercase(this.nestedInput!));
          this.nestedInput.addEventListener('blur', () => this.applyUppercase(this.nestedInput!));
          this.nestedInput.addEventListener('paste', (e) => this.handlePasteOnNested(e, this.nestedInput!));
        }
      }, 0);
    }
  }

  private applyUppercase(input: HTMLInputElement): void {
    if (this.isApplying) return;
    this.isApplying = true;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    input.value = input.value.toUpperCase();
    input.setSelectionRange(start, end);
    // Disparar evento para que ng-autocomplete procese el valor en mayúsculas
    input.dispatchEvent(new Event('input', { bubbles: true }));
    this.isApplying = false;
  }

  private handlePasteOnNested(event: ClipboardEvent, input: HTMLInputElement): void {
    if (this.isApplying) return;
    this.isApplying = true;
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const upperText = pastedText.toUpperCase();
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = input.value.substring(0, start) + upperText + input.value.substring(end);
    input.value = newValue;
    input.setSelectionRange(start + upperText.length, start + upperText.length);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    this.isApplying = false;
  }

  // Listeners para inputs nativos directos
  @HostListener('input', ['$event'])
  onInput(event: InputEvent): void {
    if (!this.isNativeInput) return;
    const input = this.el.nativeElement as HTMLInputElement;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const upperValue = input.value.toUpperCase();
    input.value = upperValue;
    input.setSelectionRange(start, end);
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(upperValue, { emitEvent: false });
    }
  }

  @HostListener('blur', ['$event'])
  onBlur(event: FocusEvent): void {
    if (!this.isNativeInput) return;
    const input = this.el.nativeElement as HTMLInputElement;
    const upperValue = input.value.toUpperCase();
    input.value = upperValue;
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(upperValue, { emitEvent: true });
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (!this.isNativeInput) return;
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const upperText = pastedText.toUpperCase();
    const input = this.el.nativeElement as HTMLInputElement;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = input.value.substring(0, start) + upperText + input.value.substring(end);
    input.value = newValue;
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(newValue, { emitEvent: true });
    }
    const newPosition = start + upperText.length;
    input.setSelectionRange(newPosition, newPosition);
  }
}
